<template>
  <TWModal
      :title="$t('users.newUser')"
      :loading="loading"
      :show="show"
      @close="handleClose"
      :allow-outside-click-to-close="false"
  >
    <div slot="content">
      <form>
        <div>
          <form data-vv-scope="user-create">
            <div class="mb-4">
              <label class="mb-1 inline-block" for="user-name">{{ $t('users.name') }}</label>
              <TextInput
                  id="user-name"
                  placeholder="e.g. Jo Bloggs"
                  :value="user.name"
                  v-model="user.name"
                  :has-error="errors.has('user-create.name')"
                  data-ec="create_user_modal"
                  data-et="user_name_input"
                  v-validate="'required'"
                  data-vv-name="name"
                  :data-vv-as="$t('users.name')"
                  data-hj-suppress
              />
              <ValidationError v-show="errors.has('user-create.name')">
                <span slot="text" >
                  {{ errors.first('user-create.name') }}
                </span>
              </ValidationError>
            </div>
            <div class="mb-4">
              <label class="mb-1 inline-block" for="user-email">{{ $t('users.email') }}</label>
              <EmailInput
                  v-model.trim="user.email"
                  :has-error="errors.has('user-create.email')"
                  id="user-email"
                  placeholder="e.g. jo@example.com"
                  v-validate="'required|isTrimmedEmail'"
                  data-vv-name="email"
                  :data-vv-as="$t('users.email')"
                  data-vv-scope="user-create"
                  data-test="input-user-email"
                  data-hj-suppress
                  data-ec="create_user_modal"
                  data-et="email_input"
              />
              <ValidationError v-show="errors.has('user-create.email')">
                <span slot="text" >
                  {{ errors.first('user-create.email') }}
                </span>
              </ValidationError>
            </div>
            <div>
              <div class="mb-4">
                <label class="mb-1 inline-block" for="user-role">{{ $t('users.role') }}</label>
                <p class="float-right mb-0 text-14"><a href="https://signable.help/users-teams-roles-explained/" target="_blank">{{ $t('users.learn-more-about-roles') }}</a></p>
                <el-select
                    id="user-role"
                    class="w-full"
                    v-model="user.role"
                    data-vv-name="role"
                    :data-vv-as="$t('users.role')"
                    data-vv-scope="user-create"
                    v-validate="'required'"
                    :class="{ 'select-error': errors.has('user-create.role') }"
                >
                  <el-option
                      v-for="item in userRoles"
                      :key="item"
                      :label="$t(`users.roles.${item}`)"
                      :value="item"
                      data-ec="create_user_modal"
                      :data-et="`opt_role_selected_${item}`">
                  </el-option>
                </el-select>
                <ValidationError v-show="errors.has('user-create.role')">
                  <span slot="text" >
                    {{ errors.first('user-create.role') }}
                  </span>
                </ValidationError>
              </div>
              <div class="mb-2">
                <label class="mb-1 inline-block">{{ $t('users.add-them-to-a-team') }}</label>
                <user-team-selector
                    v-model="user.groups"
                    :users-groups="user.groups"
                    :groups="groups"
                    data-ec="create_user_modal"
                    @select="handleTeamSelected"
                    @remove="handleTeamRemoved"
                ></user-team-selector>
              </div>
            </div>
          </form>
        </div>
      </form>
    </div>
    <div slot="controls" class="flex w-full justify-between">
      <button
          type="button"
          id="cancel-create-user-button"
          class="tw-button mr-2"
          @click="handleClose"
          data-ec="create_user_modal"
          data-et="btn_cancel">
        <icon name="close" />
        <span>{{ $t('functional.cancel') }}</span>
      </button>
      <button
          type="button"
          id="create-user-button"
          class="tw-button tw-button-cta"
          @click="handleCreateUser"
          data-ec="create_user_modal"
          data-et="btn_save">
        <icon name="check" />
        <span>{{ $t('users.saveUser') }}</span>
      </button>
    </div>
  </TWModal>
</template>

<script>
import TWModal from '@/components/tailwind/Modal.vue';
import TextInput from '@/components/tailwind/forms/TextInput.vue';
import EmailInput from '@/components/tailwind/forms/EmailInput.vue';
import { mapGetters } from 'vuex';
import ValidationError from '@/components/tailwind/forms/ValidationError.vue';
import UserTeamSelector from '@/components/tailwind/UserTeamSelector.vue';
import { logAmplitudeEvent } from '@/utils/analytics';

export default {
  name: 'CreateUserModal',
  inject: {
    $validator: '$validator',
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    UserTeamSelector,
    ValidationError,
    TWModal,
    TextInput,
    EmailInput,
  },
  data() {
    return {
      user: {
          name: null,
          email: null,
          role: '',
          groups: [],
      },
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      userRoles: 'roles/userRoles',
      groups: 'groups/pagedResults',
    }),
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.$store.dispatch('groups/PAGINATE', { page: 1, pageLimit: 45 });
      }
    },
  },
  methods: {
    handleTeamSelected() {
      logAmplitudeEvent('create_user_modal--added_team_via_dropdown');
    },
    handleTeamRemoved() {
      logAmplitudeEvent('create_user_modal--removed_team_via_dropdown');
    },
    handleCreateUser() {
      this.$validator.validateAll('user-create').then((validated) => {
        if (validated) {
          this.loading = true;
          this.$store.dispatch('users/CREATE', this.user)
              .then(() => {
                this.resetComponent();
                this.$emit('user-created');
              })
              .finally(() => {
                this.loading = false;
              });
        }
      });
    },
    handleClose($event) {
      if ($event && $event.target.classList.contains('multiselect__input')) {
        document.getElementById('create-user-button').focus();
        return;
      }

      // Pause Validator
      this.$validator.pause();

      // Reset the component to the initial state when created
      this.resetComponent();
      this.$emit('close');
    },
    resetComponent() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
  },
};
</script>

<style scoped>

</style>
